import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle2, Rocket, PartyPopper } from "lucide-react";
import { apiBaseUrl, authHeaders } from "./shared";

// Guided, revisitable walk-through of the 15 fleet.* system_properties a
// new fleet admin actually needs to look at before trusting this
// platform's numbers -- NOT a one-time setup gate (no "onboarding
// complete" flag anywhere), just a friendlier front door onto the same
// rows FleetCostSettings.jsx/FleetOperationalSettings.jsx already edit.
// Each field carries a plain-English `hint` (what it does, no internal
// jargon) and a separate `example` -- a worked, concrete scenario with
// real numbers -- rendered as its own callout rather than folded into
// the hint sentence, since a buried "e.g. 0.30" is easy to skim past.
const FIELDS = [
  {
    key: "fleet.insurance_cost_per_mile", label: "Insurance", type: "currency", unit: "per mile", step: "cost",
    hint: "How much this fleet pays in insurance for every mile a truck drives. This gets added automatically to the cost of every load, so profit numbers stay honest about what insurance actually eats into.",
    example: "Set to $0.30/mile: a 500-mile load carries $150 of insurance cost (500 × $0.30). Raising this number makes every load correctly look less profitable -- it isn't a rounding knob, it should match what this fleet really pays.",
  },
  {
    key: "fleet.office_payroll_cost_per_mile", label: "Office payroll & expenses", type: "currency", unit: "per mile", step: "cost",
    hint: "Your dispatchers, admin staff, and office overhead cost money too -- this spreads that cost across every mile driven, so a load's reported profit reflects the real cost of running the business, not just fuel and the driver.",
    example: "Set to $0.30/mile: a 1,000-mile load carries $300 of office overhead in its total cost, right alongside fuel and driver pay.",
  },
  {
    key: "fleet.maintenance_cost_per_mile", label: "Maintenance", type: "currency", unit: "per mile", step: "cost",
    hint: "A set-aside for tires, oil changes, brakes, and the repairs that come from simply putting miles on a truck -- a real ongoing cost, not a one-time expense.",
    example: "If this fleet spends about $20,000/year on maintenance for a truck that runs 100,000 miles/year, that works out to $0.20/mile -- divide actual annual spend by actual annual miles to get this fleet's real number.",
  },
  {
    key: "fleet.equipment_price_per_week", label: "Equipment price", type: "currency", unit: "per week", step: "cost",
    hint: "What it really costs to own or lease one truck+trailer for a week -- the loan or lease payment, permits, and depreciation. This is split evenly across the week and folded into every load run that week.",
    example: "Set to $1,000/week: that truck needs to earn at least $1,000 across all its loads that week just to break even on equipment. Reports' Weekly Equipment Cost Coverage widget shows whether each truck is on pace to clear it.",
  },
  {
    key: "fleet.wait_time_cost_per_hour", label: "Wait time cost", type: "currency", unit: "per hour", step: "cost",
    hint: "What it really costs when a truck sits idle at a pickup or delivery instead of driving -- not detention pay from the broker, but what that hour of the truck's time could have earned elsewhere.",
    example: "Set to $30/hour: a driver stuck waiting 3 hours at a shipper effectively costs the fleet $90 in lost opportunity (3 × $30), which gets factored into route comparisons and negotiation.",
  },

  {
    key: "fleet.min_guaranteed_pay_miles", label: "Minimum guaranteed pay miles", type: "number", unit: "miles", step: "driver-pay",
    hint: "The shortest load a per-mile driver is ever paid as. Book them on something shorter than this, and they still get paid as if it were this many miles -- a livable-minimum-payout guarantee so a short local run doesn't shortchange them.",
    example: "Set to 500 miles: a driver runs a 300-mile load at $0.60/mile. Normally that's $180, but the floor pays them 500 × $0.60 = $300 instead. Only driver pay is affected -- fuel, insurance, and other costs always use the real 300 miles. Hourly and fixed-daily drivers aren't affected at all.",
  },
  {
    key: "fleet.min_guaranteed_pay_uses_total_miles", label: "Guarantee applies to total miles (incl. deadhead)", type: "boolean", step: "driver-pay",
    hint: "Controls whether the floor above counts only the paid freight run, or the driver's whole trip including the empty (deadhead) miles driven just to reach the pickup.",
    example: "Driver deadheads 200 miles to reach a 100-mile load, floor is 500 miles, rate is $0.60/mile. OFF (default): the 100 loaded miles floor up to 500 ($300), PLUS the 200 deadhead miles paid separately at real distance ($120) = $420 total. ON: the whole 300-mile trip floors up to 500 miles combined ($300 total) -- no separate deadhead pay on top.",
  },

  {
    key: "fleet.min_miles_between_fuel_stops", label: "Minimum miles between fuel stops", type: "number", unit: "miles", step: "fuel-matching",
    hint: "Stops the route planner from scheduling two fuel stops too close together just to chase a slightly cheaper price a few miles down the road.",
    example: "Set to 300 miles: if the planner wants to add a stop only 50 miles after the last one purely to save a few cents a gallon, it skips it and waits for a stop further along instead. It never skips a stop the truck genuinely needs (tank running low) -- only discretionary ones. Set to 0 to turn this behavior off entirely.",
  },
  {
    key: "fleet.truck_search_radius_miles", label: "Truck search radius", type: "number", unit: "miles", step: "fuel-matching",
    hint: "How far from a load's pickup point the system looks when searching for available trucks to match it -- used by broker-call matching and the next-load search.",
    example: "Set to 100 miles: only trucks within 100 miles of the pickup show up as candidates. In a busy freight region that keeps results fast and relevant; in a sparse region, widening this to 200+ miles may be needed to find any truck at all -- but every extra mile searched costs a bit more in routing calls.",
  },
  {
    key: "fleet.track_unassigned_truck_miles", label: "Track idle/unassigned truck miles", type: "boolean", step: "fuel-matching",
    hint: "Turns on tracking of every mile a truck drives while it has no load assigned -- the raw data behind the Idle Miles report.",
    example: "Leave this on by default. Turning it off doesn't change any pricing or matching -- it only stops this one report's data from being collected, so only turn it off if this fleet specifically doesn't want idle-miles tracked.",
  },

  {
    key: "fleet.eta_drift_alert_hours", label: "ETA drift alert threshold", type: "number", unit: "hours", step: "alerts",
    hint: "How far a truck's live, updated arrival estimate can slip from what was originally promised before you get warned about it on the Alerts page.",
    example: "Set to 2 hours: a load was quoted to arrive by 3:00 PM. Live tracking now says 5:15 PM -- a 2.25-hour drift, past the 2-hour threshold, so an alert fires. Lower this to catch smaller slips sooner; raise it to ignore normal noise like traffic or a longer-than-usual fuel stop.",
  },
  {
    key: "fleet.eta_drift_critical_hours", label: "ETA drift critical threshold", type: "number", unit: "hours", step: "alerts",
    hint: "How far past the ETA drift alert threshold a drift has to get before it escalates from a warning to a critical alert on the Alerts page and Control Tower.",
    example: "Set to 24 hours: a load already flagged for a 3-hour drift keeps showing as a warning until the drift reaches 24 hours, at which point it escalates to critical. Should always be set well above the alert threshold above it -- otherwise every drift that warrants a warning would also immediately count as critical.",
  },
  {
    key: "fleet.route_deviation_alert_miles", label: "Off-route alert distance", type: "number", unit: "miles", step: "alerts",
    hint: "How far a truck's actual GPS position can stray from its planned route before it's flagged as off-route.",
    example: "Set to 10 miles: a truck spotted 12 miles off its planned path triggers the alert -- but only after two readings about 2 minutes apart both confirm it, so one bad GPS ping alone won't cause a false alarm.",
  },
  {
    key: "fleet.toll_overage_alert_usd", label: "Toll overage alert amount", type: "currency", unit: "over planned", step: "alerts",
    hint: "How much more than expected a truck's real, already-paid tolls can run before you're warned something's off with that route's toll estimate.",
    example: "Set to $15: a route was planned to cost $40 in tolls, but the truck actually paid $58 -- an $18 overage, past the $15 threshold, so it flags on the Alerts page. Lower this to catch smaller overages, raise it to ignore normal toll-estimate rounding.",
  },
  {
    key: "fleet.motive_stale_alert_minutes", label: "Motive sync stale alert", type: "number", unit: "minutes", step: "alerts",
    hint: "How long this fleet can go without a successful GPS/ELD sync from Motive before you're warned the connection itself might be broken.",
    example: "Motive syncs automatically every 3 minutes. Set this to 5 minutes: a normal 4-minute gap won't trip anything, but a real 20-minute outage will -- keep it a bit above the real sync interval so ordinary jitter doesn't cause constant false alarms.",
  },
  {
    key: "fleet.manual_driver_assignment_grace_minutes", label: "Manual assignment grace period", type: "number", unit: "minutes", step: "alerts",
    hint: "When a dispatcher manually swaps a truck's driver by hand, this is how long that change is protected from being silently undone by the next automatic Motive sync.",
    example: "Set to 15 minutes: a dispatcher manually reassigns a driver at 2:00 PM. Any automatic sync that runs before 2:15 PM won't overwrite that change, giving Motive's own data time to catch up. Raise this if manual changes are still getting reverted too often; lower it if stale manual overrides linger longer than they should.",
  },

  {
    key: "fleet.lane_history_radius_miles", label: "Lane history search radius", type: "number", unit: "miles", step: "lane-history",
    hint: "How far a past load's real origin and destination can be from a new load's origin/destination and still count as \"the same lane\" on the Recent Loads on This Lane panel (Check Load / Log Broker Call). Matched by real distance, not by matching the city name as text -- a full street address a few miles outside downtown still counts.",
    example: "Set to 30 miles: pricing a load from downtown Chicago pulls in a past load that picked up from a warehouse 18 miles outside the city, since it's within the 30-mile radius. Widen this in a sparse region to surface more history; narrow it in a dense one to keep results strictly same-metro.",
  },
  {
    key: "fleet.lane_history_lookback_days", label: "Lane history lookback window", type: "number", unit: "days", step: "lane-history",
    hint: "How far back the Recent Loads on This Lane panel looks for past loads on the same lane. A rate from outside this window never shows, even if it's the only one on file for that lane.",
    example: "Set to 90 days: a load booked on this lane 60 days ago shows up as recent history; one booked 120 days ago doesn't, even though it's the same lane -- keeps the reference price relevant to today's market instead of surfacing something stale.",
  },

  {
    key: "fleet.autoconfirm_offhours_mode", label: "Auto-confirm mode", type: "select",
    options: [{ value: "shadow", label: "Shadow (observe only)" }, { value: "active", label: "Active (writes pickup/delivery)" }],
    default: "shadow", step: "auto-confirm",
    hint: "Shadow just records what it WOULD do (safe). Active actually stamps pickup/delivery from GPS evidence. Setting this back to Shadow is the instant kill switch.",
    example: "Start in Shadow to watch it for a few days, then switch to Active once the confirmations look right.",
  },
  {
    key: "fleet.autoconfirm_offhours_enabled", label: "Enable auto-confirm (master)", type: "boolean", step: "auto-confirm",
    hint: "Master on/off for the whole engine. Off = nothing auto-confirms regardless of the switches below.",
  },
  {
    key: "fleet.autoconfirm_offhours_pickup_enabled", label: "Auto-confirm pickups", type: "boolean", step: "auto-confirm",
    hint: "Allow automatic pickup confirmation when a truck dwelled at the origin then drove away.",
  },
  {
    key: "fleet.autoconfirm_offhours_delivery_enabled", label: "Auto-confirm deliveries", type: "boolean", step: "auto-confirm",
    hint: "Allow automatic delivery confirmation when a truck dwelled at the destination then drove away. Also stops a delivered-but-unmarked load from absorbing the truck's next fuel/tolls.",
  },
  {
    key: "fleet.autoconfirm_always_on", label: "Run 24/7 (ignore off-hours window)", type: "boolean", step: "auto-confirm",
    hint: "On = runs around the clock. Off = only acts evenings/overnight, leaving daytime stops to dispatchers.",
  },
  {
    key: "fleet.autoconfirm_finalize_financials", label: "Close fuel/toll finances on auto-delivery", type: "boolean", step: "auto-confirm",
    hint: "On = an auto-delivery also closes the load's fuel/toll attribution so nothing further attaches to a finished load. Off = marked delivered, but a human closes the money.",
  },
  {
    key: "fleet.autoconfirm_offhours_require_appointment", label: "Require appointment-window match", type: "boolean", step: "auto-confirm",
    hint: "On (recommended) = only auto-confirm when the dwell lines up with the load's scheduled appointment -- a precision guard. Off = confirm on physical evidence alone.",
  },
  {
    key: "fleet.autoconfirm_offhours_fresh_minutes", label: "GPS freshness required", type: "number", unit: "minutes", step: "auto-confirm",
    hint: "The truck's last GPS update must be at least this fresh for auto-confirm to trust the evidence.",
    example: "Set to 20: if the last position is older than 20 minutes, auto-confirm waits for a fresh fix instead of acting on a stale one.",
  },
  {
    key: "fleet.autoconfirm_offhours_backlog_minutes", label: "Max dwell age to auto-confirm", type: "number", unit: "minutes", step: "auto-confirm",
    hint: "The live engine only confirms a stop whose dwell happened within this window (default 180 = 3h). Older already-left stops need a one-time backlog sweep.",
    example: "Set to 180: a truck that left a facility 2 hours ago can still be auto-confirmed; one that left 2 days ago is left for a human or a sweep.",
  },
  {
    key: "fleet.auto_pickup_radius_miles", label: "Pickup geofence radius", type: "number", unit: "miles", step: "auto-confirm",
    hint: "How close a truck must get to the ORIGIN to count as 'arrived at pickup' -- the accuracy knob behind auto-pickup. Tighter is more precise; wider is more forgiving. Default 5.",
  },
  {
    key: "fleet.auto_delivery_radius_miles", label: "Delivery geofence radius", type: "number", unit: "miles", step: "auto-confirm",
    hint: "Same for the DESTINATION: how close before it counts as 'arrived at delivery'. Governs auto-delivery accuracy. Default 5.",
  },
  {
    key: "fleet.autoconfirm_offhours_appt_grace_minutes", label: "Appointment-window grace", type: "number", unit: "minutes", step: "auto-confirm",
    hint: "When 'Require appointment-window match' is on, how early a truck may dwell before its scheduled appointment and still count as on-appointment. Default 60.",
  },
  {
    key: "fleet.autoconfirm_offhours_start", label: "Off-hours window start", type: "text", placeholder: "17:00", step: "auto-confirm",
    disabledWhen: (d) => (d["fleet.autoconfirm_always_on"] ?? "true") === "true",
    hint: "Local time (HH:MM, 24h) the off-hours window opens. Ignored while 'Run 24/7' is on -- turn 24/7 off to use a window.",
  },
  {
    key: "fleet.autoconfirm_offhours_end", label: "Off-hours window end", type: "text", placeholder: "07:00", step: "auto-confirm",
    disabledWhen: (d) => (d["fleet.autoconfirm_always_on"] ?? "true") === "true",
    hint: "Local time (HH:MM, 24h) the window closes. A window that wraps midnight (17:00 -> 07:00) is supported. Ignored while 'Run 24/7' is on.",
  },
  {
    key: "fleet.autoconfirm_offhours_include_weekends", label: "Include weekends in window", type: "boolean", step: "auto-confirm",
    disabledWhen: (d) => (d["fleet.autoconfirm_always_on"] ?? "true") === "true",
    hint: "When using a window (24/7 off), whether Saturday/Sunday count. Ignored while 'Run 24/7' is on.",
  },
  {
    key: "fleet.autoconfirm_offhours_timezone", label: "Auto-confirm timezone", type: "text", placeholder: "America/Chicago", step: "auto-confirm",
    disabledWhen: (d) => (d["fleet.autoconfirm_always_on"] ?? "true") === "true",
    hint: "IANA timezone the window/weekend rules use. Ignored while 'Run 24/7' is on. Falls back to the fleet default timezone if blank.",
  },
  {
    key: "fleet.min_fuel_stop_savings_usd", label: "Minimum fuel-stop savings", type: "currency", unit: "to add a stop", step: "fuel-matching",
    hint: "The optimizer only schedules a discretionary extra fuel stop if it saves at least this much (default $5). Mandatory stops (tank near reserve) are unaffected.",
    example: "Set to $5: a detour that would save $3 is skipped; one that saves $12 is taken.",
  },
  {
    key: "fleet.critical_alert_sms_recipients", label: "Critical-alert SMS recipients", type: "text", placeholder: "+13125550199, +13125550142", step: "alerts",
    hint: "Comma-separated phone numbers (E.164, e.g. +13125550199) that get SMS for CRITICAL alerts. Blank disables critical-alert texting. Non-critical alerts are never texted.",
  },
  {
    key: "fleet.default_timezone", label: "Fleet default timezone", type: "text", placeholder: "America/Chicago", step: "cost",
    hint: "The fleet's base IANA timezone (e.g. America/Chicago), used as the fallback wherever a more specific timezone isn't set. Set it to where the operation is actually run.",
  },
];

const STEPS = [
  { key: "welcome", title: "Welcome" },
  { key: "cost", title: "Cost Inputs", blurb: "The real per-mile and per-week costs that get folded into every load's profit numbers." },
  { key: "driver-pay", title: "Driver Pay", blurb: "How a short load's minimum guaranteed driver payout is calculated." },
  { key: "fuel-matching", title: "Fuel & Matching", blurb: "Fuel-stop spacing and how wide dispatch searches for a truck near a pickup." },
  { key: "alerts", title: "Alerts & Thresholds", blurb: "When this fleet wants to be warned about drift, overages, and stale data." },
  { key: "lane-history", title: "Lane History", blurb: "How wide and how far back the Recent Loads on This Lane panel looks when pricing a new load." },
  { key: "auto-confirm", title: "Auto-Confirm", blurb: "Whether AIVAN automatically marks pickups/deliveries from GPS evidence (truck dwelled at the facility then drove away), and the guards around it. Start in Shadow to watch it before going Active." },
  { key: "done", title: "Done" },
];

function defaultDraft(f) {
  if (f.type === "boolean") return "true";
  if (f.type === "select") return f.default ?? (f.options?.[0]?.value ?? "");
  if (f.type === "text") return "";
  return "0";
}

export default function Onboarding() {
  const [rows, setRows] = useState({}); // sip_name -> { id, sip_value }
  const [drafts, setDrafts] = useState({}); // sip_name -> string being edited
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [savedAt, setSavedAt] = useState(null);
  const [stepIndex, setStepIndex] = useState(0);
  const [maxStepIndex, setMaxStepIndex] = useState(0);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    axios
      .get(`${apiBaseUrl}/system_properties/?limit=500`, authHeaders())
      .then((res) => {
        const records = res.data.records || res.data || [];
        const bySipName = {};
        records.forEach((r) => {
          if (FIELDS.some((f) => f.key === r.sip_name)) bySipName[r.sip_name] = r;
        });
        setRows(bySipName);
        const nextDrafts = {};
        FIELDS.forEach((f) => {
          nextDrafts[f.key] = bySipName[f.key]?.sip_value ?? defaultDraft(f);
        });
        setDrafts(nextDrafts);
      })
      .catch((err) => setError(err.response?.data?.detail || err.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const currentStep = STEPS[stepIndex];
  const stepFields = FIELDS.filter((f) => f.step === currentStep.key);
  const dirtyKeys = stepFields.filter((f) => drafts[f.key] !== (rows[f.key]?.sip_value ?? defaultDraft(f))).map((f) => f.key);
  const hasInvalid = dirtyKeys.some((key) => {
    const f = FIELDS.find((x) => x.key === key);
    if (f.type === "boolean" || f.type === "select" || f.type === "text") return false;
    return drafts[key] === "" || Number.isNaN(Number(drafts[key]));
  });

  function goTo(index) {
    if (index > maxStepIndex) return; // no skipping ahead past unvisited steps
    setSavedAt(null);
    setStepIndex(index);
  }

  function goNext() {
    const next = Math.min(stepIndex + 1, STEPS.length - 1);
    setMaxStepIndex((m) => Math.max(m, next));
    setStepIndex(next);
  }

  function goBack() {
    setSavedAt(null);
    setStepIndex((i) => Math.max(i - 1, 0));
  }

  function handleContinue() {
    if (!stepFields.length || !dirtyKeys.length) {
      goNext();
      return;
    }
    if (hasInvalid) return;
    // Same guard as FleetCostSettings.jsx: a dirty key with no matching row
    // means the initial load hasn't finished (or failed) for that property.
    const missingRows = dirtyKeys.filter((key) => !rows[key]);
    if (missingRows.length) {
      setError("Some properties haven't finished loading yet -- refresh the page and try again.");
      return;
    }
    setSaving(true);
    setError(null);
    Promise.all(
      dirtyKeys.map((key) => {
        const f = FIELDS.find((x) => x.key === key);
        const row = rows[key];
        const value = (f.type === "boolean" || f.type === "select" || f.type === "text") ? drafts[key] : String(Number(drafts[key]));
        return axios.put(`${apiBaseUrl}/system_properties/${row.id}`, { sip_value: value }, authHeaders());
      })
    )
      .then(() => {
        // Merge the just-saved values into rows locally instead of a full
        // reload -- a full load() would blow away any unsaved edits the
        // admin already typed into a step further ahead (Back/Forward
        // between steps doesn't require saving each one in order).
        setRows((prev) => {
          const next = { ...prev };
          dirtyKeys.forEach((key) => {
            const f = FIELDS.find((x) => x.key === key);
            const value = (f.type === "boolean" || f.type === "select" || f.type === "text") ? drafts[key] : String(Number(drafts[key]));
            next[key] = { ...next[key], sip_value: value };
          });
          return next;
        });
        setSavedAt(new Date());
        goNext();
      })
      .catch((err) => setError(err.response?.data?.detail || err.message))
      .finally(() => setSaving(false));
  }

  return (
    <div>
      <div style={styles.header}>
        <h1 style={styles.title}>Fleet Setup</h1>
        <div style={styles.subtitle}>
          A guided walk-through of this fleet's cost, driver-pay, fuel/matching, alert, and lane-history settings.
          Every value below is already pre-filled with this fleet's current saved setting -- come back
          any time, nothing here is one-time-only. For the full list (including HERE/Claude spend
          ceilings), see{" "}
          <Link to="/dispatch/fleet-cost-settings" style={{ color: "var(--brand)" }}>Fleet Cost Settings</Link>
          {" "}and{" "}
          <Link to="/dispatch/fleet-operational-settings" style={{ color: "var(--brand)" }}>Fleet Operational Settings</Link>.
        </div>
      </div>

      <div style={styles.stepper}>
        {STEPS.map((s, i) => {
          const isActive = i === stepIndex;
          const isDone = i < stepIndex || (i <= maxStepIndex && i !== stepIndex);
          const clickable = i <= maxStepIndex;
          return (
            <React.Fragment key={s.key}>
              <div
                style={{ ...styles.stepItem, cursor: clickable ? "pointer" : "default" }}
                onClick={() => clickable && goTo(i)}
              >
                <div
                  style={{
                    ...styles.stepDot,
                    ...(isDone ? styles.stepDotDone : {}),
                    ...(isActive ? styles.stepDotActive : {}),
                  }}
                >
                  {isDone ? <CheckCircle2 size={12} /> : i + 1}
                </div>
                <div style={{ ...styles.stepLabel, ...(isActive || isDone ? { color: "var(--ink)" } : {}) }}>{s.title}</div>
              </div>
              {i < STEPS.length - 1 && (
                <div style={{ ...styles.stepConnector, ...(i < maxStepIndex ? { background: "var(--brand)" } : {}) }} />
              )}
            </React.Fragment>
          );
        })}
      </div>

      {error && <div style={styles.errorBox}>{error}</div>}

      <div style={styles.card}>
        {currentStep.key === "welcome" && (
          <div style={styles.introBody}>
            <Rocket size={28} color="var(--brand)" />
            <div style={styles.introTitle}>Let's set up this fleet's numbers</div>
            <div style={styles.introText}>
              This platform prices every load, ranks every truck match, and reports every dollar of
              profit using a handful of real-world numbers this fleet has to supply -- what a mile of
              insurance actually costs, how wide to search for a truck, when to sound an alarm. We've
              already seeded sensible defaults, so nothing below is blank -- just review each one against
              this fleet's real numbers and adjust what needs it.
            </div>
            <ul style={styles.introList}>
              {STEPS.slice(1, -1).map((s) => (
                <li key={s.key} style={styles.introListItem}>
                  <strong>{s.title}</strong> -- {s.blurb}
                </li>
              ))}
            </ul>
          </div>
        )}

        {currentStep.key === "done" && (
          <div style={styles.introBody}>
            <PartyPopper size={28} color="var(--brand)" />
            <div style={styles.introTitle}>You're set up</div>
            <div style={styles.introText}>
              This fleet's cost, driver-pay, fuel/matching, and alert settings are saved and already
              live everywhere they're read -- trip costing, matching, negotiation floors, and the Alerts
              page. Come back to this guide any time from the sidebar, or jump straight to the full
              settings pages for anything not covered here (HERE/Claude spend ceilings included):
            </div>
            <div style={styles.doneLinks}>
              <Link to="/dispatch/fleet-cost-settings" style={styles.doneLinkBtn}>Fleet Cost Settings</Link>
              <Link to="/dispatch/fleet-operational-settings" style={styles.doneLinkBtn}>Fleet Operational Settings</Link>
            </div>
          </div>
        )}

        {stepFields.length > 0 && (
          <>
            <div style={styles.stepBodyHeader}>
              <div style={styles.stepBodyTitle}>{currentStep.title}</div>
              {currentStep.blurb && <div style={styles.stepBodyBlurb}>{currentStep.blurb}</div>}
            </div>
            {stepFields.map((f, i) => {
              const draft = drafts[f.key] ?? defaultDraft(f);
              const isDirty = draft !== (rows[f.key]?.sip_value ?? defaultDraft(f));
              const isNum = f.type !== "boolean" && f.type !== "select" && f.type !== "text";
              const isInvalid = isNum && (draft === "" || Number.isNaN(Number(draft)));
              const isOn = draft === "true";
              // Window fields only apply when "Run 24/7" is off -- grey them out
              // otherwise so a stale value can't look active (engine ignores it too).
              const dep = f.disabledWhen ? f.disabledWhen(drafts) : false;
              const dis = loading || saving || dep;
              return (
                <div
                  key={f.key}
                  style={{ ...styles.row, opacity: dep ? 0.5 : 1, borderBottom: i === stepFields.length - 1 ? "none" : "1px solid var(--line)" }}
                >
                  <div style={styles.rowTop}>
                    <div style={styles.rowLabel}>{f.label}{dep && <span style={styles.depNote}> — n/a while 24/7 is on</span>}</div>
                    {f.type === "boolean" ? (
                      <button
                        type="button"
                        onClick={() => setDrafts((d) => ({ ...d, [f.key]: isOn ? "false" : "true" }))}
                        disabled={dis}
                        style={{ ...styles.toggleTrack, background: isOn ? "var(--brand)" : "var(--line)" }}
                        aria-pressed={isOn}
                      >
                        <span style={{ ...styles.toggleThumb, transform: isOn ? "translateX(16px)" : "translateX(0)" }} />
                      </button>
                    ) : f.type === "select" ? (
                      <select
                        value={draft}
                        onChange={(e) => setDrafts((d) => ({ ...d, [f.key]: e.target.value }))}
                        disabled={dis}
                        style={{ ...styles.input, width: "auto", borderColor: isDirty ? "var(--brand)" : "var(--line)" }}
                      >
                        {f.options.map((o) => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </select>
                    ) : f.type === "text" ? (
                      <input
                        type="text"
                        value={draft}
                        placeholder={f.placeholder || ""}
                        onChange={(e) => setDrafts((d) => ({ ...d, [f.key]: e.target.value }))}
                        style={{ ...styles.input, width: 180, textAlign: "left", borderColor: isDirty ? "var(--brand)" : "var(--line)" }}
                        disabled={dis}
                      />
                    ) : (
                      <div style={styles.inputGroup}>
                        {f.type === "currency" && <span style={styles.dollarSign}>$</span>}
                        <input
                          type="number"
                          step="0.01"
                          value={draft}
                          onChange={(e) => setDrafts((d) => ({ ...d, [f.key]: e.target.value }))}
                          style={{ ...styles.input, borderColor: isInvalid ? "#dc2626" : isDirty ? "var(--brand)" : "var(--line)" }}
                          disabled={dis}
                        />
                        <span style={styles.unit}>{f.unit}</span>
                      </div>
                    )}
                  </div>
                  {f.hint && <div style={styles.rowHint}>{f.hint}</div>}
                  {f.example && (
                    <div style={styles.exampleBox}>
                      <span style={styles.exampleLabel}>Example</span>
                      <span style={styles.exampleText}>{f.example}</span>
                    </div>
                  )}
                </div>
              );
            })}
          </>
        )}
      </div>

      <div style={styles.footer}>
        {stepIndex > 0 && (
          <button style={styles.backBtn} onClick={goBack} disabled={saving}>
            <ArrowLeft size={14} /> Back
          </button>
        )}
        {currentStep.key !== "done" && (
          <button
            style={{ ...styles.continueBtn, opacity: hasInvalid || saving || loading ? 0.5 : 1 }}
            onClick={handleContinue}
            disabled={hasInvalid || saving || loading}
          >
            {saving ? "Saving…" : currentStep.key === "welcome" ? "Get Started" : "Save & Continue"} <ArrowRight size={14} />
          </button>
        )}
        {currentStep.key === "done" && (
          <button style={styles.continueBtn} onClick={() => goTo(0)}>
            Restart guide
          </button>
        )}
        {savedAt && !dirtyKeys.length && <span style={styles.savedNote}>Saved at {savedAt.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}</span>}
        {dirtyKeys.length > 0 && !hasInvalid && (
          <span style={styles.dirtyNote}>{dirtyKeys.length} unsaved change{dirtyKeys.length > 1 ? "s" : ""}</span>
        )}
      </div>
    </div>
  );
}

const styles = {
  header: { marginBottom: 20 },
  title: { fontSize: 22, fontWeight: 800, color: "var(--ink)", margin: 0 },
  subtitle: { fontSize: 13, color: "var(--muted)", marginTop: 4, maxWidth: 700, lineHeight: 1.5 },

  stepper: { display: "flex", alignItems: "center", maxWidth: 760, marginBottom: 18 },
  stepItem: { display: "flex", flexDirection: "column", alignItems: "center", gap: 6, flexShrink: 0 },
  stepDot: {
    width: 24, height: 24, borderRadius: "50%", border: "1.5px solid var(--line)", color: "var(--muted)",
    display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700,
    background: "var(--card)", flexShrink: 0,
  },
  stepDotDone: { background: "var(--brand)", borderColor: "var(--brand)", color: "#fff" },
  stepDotActive: { borderColor: "var(--brand)", color: "var(--brand)" },
  stepLabel: { fontSize: 10.5, fontWeight: 600, color: "var(--muted)", whiteSpace: "nowrap" },
  stepConnector: { flex: 1, height: 1.5, background: "var(--line)", marginBottom: 16, minWidth: 12 },

  errorBox: {
    background: "rgba(220,38,38,0.06)", border: "1px solid rgba(220,38,38,0.2)", color: "#dc2626",
    borderRadius: "var(--radius-md)", padding: "12px 16px", fontSize: 13, marginBottom: 16, maxWidth: 760,
  },

  card: {
    background: "var(--card)", border: "1px solid var(--line)", borderRadius: "var(--radius-lg)",
    boxShadow: "var(--shadow-xs)", maxWidth: 760, overflow: "hidden",
  },
  introBody: { padding: "28px 28px 24px" },
  introTitle: { fontSize: 17, fontWeight: 800, color: "var(--ink)", marginTop: 12 },
  introText: { fontSize: 13, color: "var(--muted)", lineHeight: 1.6, marginTop: 8, maxWidth: 620 },
  introList: { marginTop: 16, paddingLeft: 18, display: "flex", flexDirection: "column", gap: 6 },
  introListItem: { fontSize: 13, color: "var(--ink-2)", lineHeight: 1.5 },
  doneLinks: { display: "flex", gap: 10, marginTop: 18 },
  doneLinkBtn: {
    fontSize: 12.5, fontWeight: 700, color: "var(--brand)", background: "rgba(29, 111, 216,0.08)",
    border: "1px solid rgba(29, 111, 216,0.25)", borderRadius: 8, padding: "8px 14px", textDecoration: "none",
  },

  stepBodyHeader: { padding: "18px 20px 4px" },
  stepBodyTitle: { fontSize: 15, fontWeight: 700, color: "var(--ink)" },
  stepBodyBlurb: { fontSize: 12.5, color: "var(--muted)", marginTop: 3 },

  row: { padding: "16px 20px" },
  rowTop: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16 },
  rowLabel: { fontSize: 13.5, fontWeight: 600, color: "var(--ink)" },
  depNote: { fontSize: 11, fontWeight: 500, color: "var(--muted)", fontStyle: "italic" },
  rowHint: { fontSize: 12, color: "var(--muted)", marginTop: 6, lineHeight: 1.55, maxWidth: 640 },
  exampleBox: {
    display: "flex", gap: 8, marginTop: 8, maxWidth: 640, padding: "8px 10px",
    background: "rgba(29, 111, 216,0.06)", border: "1px solid rgba(29, 111, 216,0.18)", borderRadius: 7,
  },
  exampleLabel: {
    fontSize: 9.5, fontWeight: 800, color: "var(--brand)", textTransform: "uppercase", letterSpacing: ".04em",
    flexShrink: 0, paddingTop: 1,
  },
  exampleText: { fontSize: 12, color: "var(--ink-2)", lineHeight: 1.55 },
  toggleTrack: {
    position: "relative", width: 36, height: 20, borderRadius: 999, border: "none", cursor: "pointer",
    padding: 2, flexShrink: 0,
  },
  toggleThumb: {
    display: "block", width: 16, height: 16, borderRadius: "50%", background: "#fff",
    transition: "transform 0.15s ease", boxShadow: "0 1px 2px rgba(0,0,0,0.25)",
  },
  inputGroup: { display: "flex", alignItems: "center", gap: 6, flexShrink: 0 },
  dollarSign: { fontSize: 13, color: "var(--muted)", fontWeight: 600 },
  input: {
    width: 90, fontSize: 13.5, fontWeight: 600, color: "var(--ink)", padding: "6px 8px",
    border: "1px solid var(--line)", borderRadius: 7, textAlign: "right",
    fontVariantNumeric: "tabular-nums",
  },
  unit: { fontSize: 12, color: "var(--muted)", width: 62 },

  footer: { display: "flex", alignItems: "center", gap: 12, marginTop: 16 },
  backBtn: {
    display: "flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 600, color: "var(--ink-2)",
    background: "var(--card)", border: "1px solid var(--line)", borderRadius: 8, padding: "9px 16px", cursor: "pointer",
  },
  continueBtn: {
    display: "flex", alignItems: "center", gap: 7, fontSize: 13, fontWeight: 700, color: "#fff",
    background: "var(--brand)", border: "none", borderRadius: 8, padding: "9px 18px", cursor: "pointer",
  },
  savedNote: { fontSize: 12, color: "#4cae6d", fontWeight: 600 },
  dirtyNote: { fontSize: 12, color: "var(--muted)" },
};
